import { retryTransient } from '@agos/api-client'
import { CounterPaymentDialog } from '@agos/manager-orders'
import {
  AuthProvider,
  CounterPaymentProvider,
  FranchiseModulesProvider,
  ThemeProvider,
  UnsavedWorkProvider,
  initI18n,
} from '@agos/shared'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { Route, Router, Switch } from 'wouter'

import { AppLayout, AuthGate } from './layout/AppLayout'
import { RouteGate } from './layout/RouteGate'
import { catchAllRoute, pathRoutes } from './module-registry'
import { ScaffoldNotice } from './ScaffoldNotice'
import { Toaster } from '@agos/ui'

initI18n()

/**
 * Query defaults copied from legacy `App.tsx`. They are not preferences: every
 * board's refresh behaviour is tuned against them.
 *
 * **FB-833, §3.158 - the reason this comment used to give was never true of the
 * setting it annotates.** It said `retry: false` is what makes *"a failing kasa
 * call surface at once rather than after four silent attempts"*. But
 * `defaultOptions.queries` governs **queries only**: the fiskal print is
 * `postFiskalReceipt`, a **direct function call** (`PaymentDialog.tsx`), the
 * closure and register writes are **mutations** - which this does not touch and
 * which no app configures at all - and the only fiskal *query* is
 * `useGetFiskalRegisters`, a read of which registers exist.
 *
 * **The reason that replaced it was about the polled boards and it is a good one**:
 * a kitchen board re-asks every eight seconds, so **three** retries with backoff
 * can outlast the next poll and a failure would surface *later* rather than sooner.
 *
 * **FB-836, §9.550 - that is an argument about three, and §9.549's shared policy
 * allows one.** Read off `@tanstack/query-core` 5.101.4's own `retryer.ts`:
 * `defaultRetryDelay` is `Math.min(1000 * 2 ** failureCount, 30000)`, and both it
 * and the retry predicate are handed the **pre-increment** count. So three retries
 * fire at 1 s, 2 s and 4 s and a failure surfaces at **~7 s**; `retryTransient`
 * retries once, at 1 s. **The shortest poll interval in the product is 1 500 ms**
 * (`JOB_POLL_MS`), the next shortest 3 000, and this board's is
 * `PAYMENT_POLL_MS = 8000` - so one retry is inside every one of them and the
 * objection dissolves.
 *
 * **And every 4xx behaves exactly as `retry: false` did**, because `worthRetrying`
 * answers false for a 400, 401, 403, 404, 409 and 422 - so the expired token, the
 * missing row and the refused right all still surface at once. Only a 5xx or a
 * dropped connection changes, and on a polled board one retry at 1 s beats waiting
 * up to 8 s (or 60 s, on the seven screens that poll at a minute) for the next poll
 * to recover.
 *
 * **What this does NOT do is convert the 76**: `TODO.md` carries 76 screens that
 * draw a nothing-state over a failed read, and their sentences are still wrong when
 * both attempts fail. It removes the commonest way to reach them - one dropped
 * packet - rather than the claim. The per-read decision is still owed.
 */
const queryClient = new QueryClient({
  defaultOptions: {
    queries: { refetchOnWindowFocus: false, retry: retryTransient },
  },
})

/**
 * The shell holds no feature code (§5): providers, the router assembly, the
 * layout chrome and the auth gate. Pages arrive from module route tables.
 *
 * Provider order is ported from legacy and matters — theme paints before auth
 * resolves so the login screen is already branded, and the module gate sits
 * inside auth because it is fetched per tenant.
 */
export function App() {
  const routes = pathRoutes()
  const notFound = catchAllRoute()
  const unauthenticatedRoutes = routes.filter((route) => route.requiresAuth === false)
  const inShell = routes.filter((route) => route.requiresAuth !== false && route.fullscreen !== true)
  // Behind the auth gate, outside the chrome: §9.6's driving surface is a phone in a driver's hand.
  const fullscreenRoutes = routes.filter(
    (route) => route.requiresAuth !== false && route.fullscreen === true,
  )

  /* §9.631, FB-944 - above `AppLayout`, because the layout both reads the flag and renders the pages that set
     it, and a component cannot consume a context it provides in the same element. At the top of the stack
     rather than around the layout: it is a ref and a setter with no fetch, so it costs a context and nothing
     else, and the fullscreen routes outside the chrome inherit it for free. */
  return (
    <UnsavedWorkProvider>
    <ThemeProvider>
      <AuthProvider>
        <FranchiseModulesProvider>
          <QueryClientProvider client={queryClient}>
            {/* §9.740, owner O111 - the till's *Zaplatiť*, which *Rozvoz - nástenka* opens on *Vydaná*. The board and
                the dialog are two modules, and §5 forbids one importing the other, so composing them is this
                shell's job: the orders module's public entry fills a slot the board reads. */}
            <CounterPaymentProvider dialog={CounterPaymentDialog}>
            <Router base={import.meta.env.BASE_URL.replace(/\/$/, '')}>
              <Switch>
                {unauthenticatedRoutes.map((route) => (
                  <Route key={route.path} path={route.path} component={route.component} />
                ))}
                {fullscreenRoutes.map((route) => (
                  <Route key={route.path} path={route.path}>
                    <AuthGate>
                      <RouteGate guard={route.guard}>
                        <route.component />
                      </RouteGate>
                    </AuthGate>
                  </Route>
                ))}
                <Route>
                  <AuthGate>
                    <AppLayout>
                      <Switch>
                        {inShell.map((route) => (
                          <Route key={route.path} path={route.path}>
                            {/* The route's guard is enforced here, not just drawn in the sidebar. */}
                            <RouteGate guard={route.guard}>
                              <route.component />
                            </RouteGate>
                          </Route>
                        ))}
                        <Route component={notFound?.component ?? ScaffoldNotice} />
                      </Switch>
                    </AppLayout>
                  </AuthGate>
                </Route>
              </Switch>
            </Router>
            </CounterPaymentProvider>
            <Toaster />
          </QueryClientProvider>
        </FranchiseModulesProvider>
      </AuthProvider>
    </ThemeProvider>
    </UnsavedWorkProvider>
  )
}
